import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../themes/Default/components/Form/components/Input";
import { Textarea } from "../../../../themes/Default/components/Form/components/Textarea";
import { Select } from "../../../../themes/Default/components/Form/components/Select";
import { groupsManagement } from "../../../../hooks/api/GroupsManagement";
import { useTranslation } from "react-i18next";
import { prepareSelect } from "../../../../services/core/Select";
import { noteTypesManagement } from "../../../../hooks/api/NoteTypesManagement/NoteTypesManagement";
import { useUserToken } from "../../../../hooks/core/UserToken";
import { roles } from "../../../../services/utils/Roles";

const AddNote = () => {
    const { t } = useTranslation("common");
    const { groupId } = useParams();
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const [noteTypes, setNoteTypes] = useState([]);
    const { useAddNoteMutation, useViewGroupMutation } = groupsManagement;
    const [addNote, { isLoading: isAddingNote }] = useAddNoteMutation();
    const { useNoteTypesListMutation } = noteTypesManagement;
    const [noteTypesList] = useNoteTypesListMutation();
    const [hasTests, setHasTests] = useState(false);
    const [hasProgress, setHasProgress] = useState(false);
    const [hasReport, setHasReport] = useState(false);
    const [viewGroup] = useViewGroupMutation();

    const tabs = [
        { title: t("groups.attendance"), onClick: () => gotoAttendanceTab(groupId) },
        { title: t("groups.planning"), onClick: () => gotoPlanningTab(groupId) },
        ...(hasProgress ? [
            { title: t('groups.progress'), onClick: () => gotoProgressTab(groupId)},
        ] : []), 
        ...(hasTests ? [
            { title: t('groups.progressTest'), onClick: () => gotoProgressTestsTab(groupId) }
        ] : []), 
        ...(hasReport ? [
            { title: t('reports.title'),onClick: () => gotoReportTab(groupId)},
        ] : []),
        { title: t("documents"), onClick: () => gotoDocumentsTab(groupId) },
        { title: t("notes"), isActive: true },
    ];

    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();
    if (
        decodedToken?.roles.includes(roles.SUPER_ADMIN) ||
        decodedToken?.roles.includes(roles.SECRETARY) ||
        decodedToken?.roles.includes(roles.DIRECTOR) || 
        decodedToken?.roles.includes(roles.DIRECTOR_OF_STUDIES)
    ) {
        tabs.push({
            title: t("settings.title"),
            onClick: () => gotoEditTab(groupId),
        });
    }

    const gotoPlanningTab = (groupId) => {
        navigate("/groups/" + groupId + "/planning");
    };

    const gotoProgressTestsTab = (groupId) => {
        navigate("/groups/" + groupId + "/tests");
    }

    const gotoReportTab = (groupId) => {
        navigate("/groups/" + groupId + "/reports");
    }

    const gotoDocumentsTab = (groupId) => {
        navigate("/groups/" + groupId + "/documents");
    };

    const gotoProgressTab = (groupId) => {
        navigate("/groups/" + groupId + "/progress");
    };

    const gotoAttendanceTab = (groupId) => {
        navigate("/groups/" + groupId + "/attendance");
    };

    const gotoEditTab = (groupId) => {
        navigate("/groups/edit/" + groupId);
    };

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        addNote({ body: values, groupId }).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
            } else {
                navigate(`/groups/${groupId}/notes`);
            }
        });
    };

    useEffect(() => {
        viewGroup(groupId).then((result) => {
            setHasTests(result?.data?.hasTests);
            setHasReport(result?.data?.hasReport);
            setHasProgress(result?.data?.hasProgress);
        });
        noteTypesList().then((result) => {
            let options = [];
            result?.data?.records.forEach((note) => {
                options.push({
                    id: note.id,
                    name: note.name,
                });
            });
            setNoteTypes(prepareSelect(options));
        });
    }, [noteTypesList]);

    return (
        <DefaultLayout>
            <TopNavigation>
                <ButtonBack to={"/groups/" + groupId + "/notes"} />
                <Button title={t("save")} isDisabled={isAddingNote} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t('groups.title')} icon="fa-user-group"/>
                    <Link title={t('notes')} />
                    <Link title={t("students.addNotes")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        type: "",
                        title: "",
                        description: "",
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select
                                        name="type"
                                        label={t("groups.noteType")}
                                        options={noteTypes}
                                        onChange={handleChange}
                                        selectedValue={values.type}
                                        error={formErrors?.type}
                                        required={true}
                                    />
                                </Field>
                                <Field>
                                    <Input 
                                        name="title" 
                                        label={t("plans.titlePlan")}
                                        value={values.title} 
                                        onChange={handleChange} 
                                        error={formErrors?.title} 
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        rows="7"
                                        name="description"
                                        label={t("calendar.description")}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={formErrors?.description}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default AddNote;
